import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Lock } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useFeatureAccess from "@/hooks/useFeatureAccess";

export const PromoCodesManagement = () => {
  const { canSaveFeature } = useFeatureAccess();
  const { canSave, reason } = canSaveFeature("promo_codes");
  
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newPromo, setNewPromo] = useState({
    code: "",
    discount_type: "percentage",
    discount_value: 10,
    min_ride_amount: 0,
    max_discount: null,
    usage_limit: null,
    per_user_limit: 1,
    is_active: true
  });

  const fetchPromoCodes = async () => {
    try {
      const res = await axios.get(`${API}/admin/promo-codes`);
      setPromoCodes(res.data);
    } catch (err) {
      toast.error("Failed to load promo codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPromoCodes(); }, []);

  const createPromoCode = async () => {
    if (!canSave) {
      toast.error(reason || "This feature is not available");
      return;
    }
    if (!newPromo.code) {
      toast.error("Please enter a promo code");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/promo-codes`, newPromo);
      toast.success("Promo code created!");
      fetchPromoCodes();
      setShowCreate(false);
      setNewPromo({ code: "", discount_type: "percentage", discount_value: 10, min_ride_amount: 0, max_discount: null, usage_limit: null, per_user_limit: 1, is_active: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create promo code");
    } finally {
      setCreating(false);
    }
  };

  const deletePromoCode = async (code) => {
    if (!canSave) {
      toast.error(reason || "This feature is not available");
      return;
    }
    if (!confirm(`Delete promo code ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${code}`);
      toast.success("Promo code deleted");
      fetchPromoCodes();
    } catch (err) {
      toast.error("Failed to delete promo code");
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="promo-codes-management">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Promo Codes</h1>
          <p className="text-muted-foreground">Create and manage discount codes</p>
        </div>
        <Button onClick={() => setShowCreate(true)} disabled={!canSave} data-testid="create-promo-btn">
          {!canSave && <Lock className="w-4 h-4 mr-2" />}
          Create Promo Code
        </Button>
      </div>

      {/* Restriction Banner */}
      {!canSave && reason && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
          <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-800">{reason}</p>
            <p className="text-sm text-amber-600">You can view promo codes but cannot create or modify them.</p>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="mb-6 p-6 bg-card rounded-xl border border-border">
          <h3 className="font-semibold mb-4">Create New Promo Code</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Code</Label>
              <Input value={newPromo.code} onChange={(e) => setNewPromo({...newPromo, code: e.target.value.toUpperCase()})} placeholder="SAVE20" data-testid="promo-code-input" />
            </div>
            <div>
              <Label>Discount Type</Label>
              <Select value={newPromo.discount_type} onValueChange={(v) => setNewPromo({...newPromo, discount_type: v, discount_value: v === 'free_ride' ? 0 : newPromo.discount_value})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                  <SelectItem value="flat">Flat Amount (₹)</SelectItem>
                  <SelectItem value="free_ride">Free Ride (100% off)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newPromo.discount_type !== "free_ride" && (
              <div>
                <Label>Discount Value</Label>
                <Input type="number" value={newPromo.discount_value} onChange={(e) => setNewPromo({...newPromo, discount_value: parseFloat(e.target.value)})} />
              </div>
            )}
            <div>
              <Label>Min Ride Amount (₹)</Label>
              <Input type="number" value={newPromo.min_ride_amount} onChange={(e) => setNewPromo({...newPromo, min_ride_amount: parseFloat(e.target.value)})} />
            </div>
            {newPromo.discount_type === "percentage" && (
              <div>
                <Label>Max Discount (₹)</Label>
                <Input type="number" value={newPromo.max_discount || ""} onChange={(e) => setNewPromo({...newPromo, max_discount: e.target.value ? parseFloat(e.target.value) : null})} placeholder="Optional" />
              </div>
            )}
            <div>
              <Label>Usage Limit (Total)</Label>
              <Input type="number" value={newPromo.usage_limit || ""} onChange={(e) => setNewPromo({...newPromo, usage_limit: e.target.value ? parseInt(e.target.value) : null})} placeholder="Unlimited" />
            </div>
            <div>
              <Label>Vehicle Types (Optional)</Label>
              <Select value={newPromo.vehicle_types?.join(',') || 'all'} onValueChange={(v) => setNewPromo({...newPromo, vehicle_types: v === 'all' ? null : v.split(',')})}>
                <SelectTrigger><SelectValue placeholder="All Vehicles" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vehicles</SelectItem>
                  <SelectItem value="bike">Bike Only</SelectItem>
                  <SelectItem value="auto">Auto Only</SelectItem>
                  <SelectItem value="car">Car Only</SelectItem>
                  <SelectItem value="bike,auto">Bike & Auto</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={createPromoCode} disabled={creating}>{creating ? "Creating..." : "Create"}</Button>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {promoCodes.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">No promo codes yet</div>
        ) : (
          promoCodes.map((promo) => (
            <div key={promo.code} className="p-4 bg-card rounded-xl border border-border flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-lg">{promo.code}</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${promo.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {promo.is_active ? "Active" : "Inactive"}
                  </span>
                  {promo.discount_type === "free_ride" && (
                    <span className="px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700">FREE RIDE</span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {promo.discount_type === "free_ride" 
                    ? "100% off - Free Ride" 
                    : promo.discount_type === "percentage" 
                      ? `${promo.discount_value}% off` 
                      : `₹${promo.discount_value} off`}
                  {promo.min_ride_amount > 0 && ` on min ₹${promo.min_ride_amount}`}
                  {promo.max_discount && ` (max ₹${promo.max_discount})`}
                  {promo.vehicle_types && ` • ${promo.vehicle_types.join(', ')}`}
                </p>
                <p className="text-xs text-muted-foreground">Used: {promo.times_used || 0}{promo.usage_limit ? ` / ${promo.usage_limit}` : ""}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => deletePromoCode(promo.code)} className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PromoCodesManagement;
